import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import {
  foyerApi,
  type ContentOwnerRecord,
  type TitleOwnerView,
} from "../../http/foyer";
import { friendlyError } from "../../utils/apiError";
import { tokens } from "../../theme";

/**
 * Which content owner a title belongs to, and the two ways staff change that
 * (`CONTENT-28`).
 *
 * TWO verbs, deliberately, mirroring the server:
 *
 *  - **Attribute** - a title nobody holds. The studio then sees it in their
 *    portal, read-only: it is a title Nenda has licensed.
 *  - **Move** - a title somebody already holds. Takes it OUT of one studio's
 *    portal and into another's, so it asks first and says exactly that.
 *
 * Never one button that does whichever applies: silently upgrading an
 * attribution into a move is how a competitor's title lands in somebody's
 * portal.
 */
export default function TitleOwnerSection({ assetId }: { assetId: string }) {
  const [view, setView] = useState<TitleOwnerView | null>(null);
  const [owners, setOwners] = useState<ContentOwnerRecord[]>([]);
  const [pick, setPick] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    Promise.all([foyerApi.getTitleOwner(assetId), foyerApi.listContentOwners()])
      .then(([v, list]) => {
        if (!alive) return;
        setView(v);
        // A disabled owner accepts no new titles; offering it is a refusal
        // waiting to happen.
        setOwners(list.filter((o) => o.enabled));
      })
      .catch((e) => alive && setError(friendlyError(e)));
    return () => {
      alive = false;
    };
  }, [assetId]);

  const held = Boolean(view?.contentOwnerId);
  const choices = owners.filter((o) => o.id !== view?.contentOwnerId);
  const target = owners.find((o) => o.id === pick);

  const run = async (move: boolean) => {
    setBusy(true);
    setError(null);
    try {
      const next = move
        ? await foyerApi.reassignTitleOwner(assetId, pick)
        : await foyerApi.attributeTitle(assetId, pick);
      setView(next);
      setPick("");
      setConfirming(false);
    } catch (e) {
      const err = e as { body?: { hint?: string } };
      setError(err.body?.hint ?? friendlyError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box data-testid="title-owner-section">
      <Typography sx={{ fontWeight: 700, fontSize: 15 }}>
        Content owner
      </Typography>
      <Typography sx={{ fontSize: 13, color: tokens.inkMuted, mb: 1.5 }}>
        The studio or provider whose portal shows this title. An attributed
        title is read-only for them - changes to it go through Nenda.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          {error}
        </Alert>
      )}

      <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1.5 }}>
        {view === null ? (
          <Typography sx={{ fontSize: 13 }}>Loading…</Typography>
        ) : held ? (
          <>
            <Typography sx={{ fontSize: 14 }} data-testid="title-owner-name">
              {view.name}
            </Typography>
            <Chip
              size="small"
              label={view.licensed ? "Licensed (read-only)" : "Their upload"}
            />
          </>
        ) : (
          <Typography sx={{ fontSize: 14, color: tokens.inkMuted }}>
            Nobody - Nenda&apos;s own. No owner&apos;s portal shows it.
          </Typography>
        )}
      </Stack>

      <Stack direction="row" gap={1} alignItems="center">
        <TextField
          select
          size="small"
          label={held ? "Move to" : "Attribute to"}
          value={pick}
          onChange={(e) => setPick(e.target.value)}
          sx={{ minWidth: 260 }}
          inputProps={{ "data-testid": "title-owner-pick" }}
          disabled={view === null || busy}
        >
          {choices.map((o) => (
            <MenuItem key={o.id} value={o.id}>
              {o.name}
            </MenuItem>
          ))}
        </TextField>
        {/* Nothing until the holder is known: before that, "held" reads
            false and the page would offer Attribute on a title somebody
            may already own. */}
        {view === null ? null : held ? (
          <Button
            variant="outlined"
            color="warning"
            disabled={!pick || busy}
            onClick={() => setConfirming(true)}
            data-testid="title-owner-move"
          >
            Move…
          </Button>
        ) : (
          <Button
            variant="contained"
            disabled={!pick || busy}
            onClick={() => void run(false)}
            data-testid="title-owner-attribute"
          >
            Attribute
          </Button>
        )}
      </Stack>

      <Dialog open={confirming} onClose={() => setConfirming(false)}>
        <DialogTitle>Move this title?</DialogTitle>
        <DialogContent>
          <Typography>
            It leaves <strong>{view?.name}</strong>&apos;s portal immediately
            and appears in <strong>{target?.name}</strong>&apos;s. This is
            recorded in the audit trail.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirming(false)}>Cancel</Button>
          <Button
            color="warning"
            variant="contained"
            disabled={busy}
            onClick={() => void run(true)}
            data-testid="title-owner-move-confirm"
          >
            Move to {target?.name}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
